import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import api from "../services/api";

function QRCodeScanner({ onClose, onResgateSucesso }) {
  const html5QrCodeRef = useRef(null);
  const iniciadoRef = useRef(false);
  const fechandoRef = useRef(false);

  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(false);

  const [operacaoQRCode, setOperacaoQRCode] = useState(null);
  const [confirmandoOperacao, setConfirmandoOperacao] = useState(false);

  async function pararScanner() {
    try {
      const scanner = html5QrCodeRef.current;

      if (scanner) {
        try {
          await scanner.stop();
        } catch {
          // ignora erro se já estiver parado
        }

        try {
          await scanner.clear();
        } catch {
          // ignora erro se já estiver limpo
        }
      }
    } finally {
      html5QrCodeRef.current = null;
      iniciadoRef.current = false;
    }
  }

  async function handleFechar() {
    if (fechandoRef.current) return;

    fechandoRef.current = true;
    setCarregando(true);

    await pararScanner();

    setCarregando(false);
    fechandoRef.current = false;

    onClose();
  }

  function obterPosicao(options) {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocalização não suportada neste navegador"));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });
  }

  function traduzirErroGeolocalizacao(error) {
    if (!error) {
      return "Não foi possível obter sua localização.";
    }

    if (typeof error.code === "number") {
      switch (error.code) {
        case 1:
          return "Permissão de localização negada. Verifique o acesso à localização no Safari.";
        case 2:
          return "Não foi possível determinar sua localização.";
        case 3:
          return "A localização demorou demais para responder.";
        default:
          return "Não foi possível obter sua localização.";
      }
    }

    return error.message || "Não foi possível obter sua localização.";
  }

  async function confirmarOperacaoQRCode() {
    if (!operacaoQRCode?.codigo) {
      return;
    }

    try {
      setConfirmandoOperacao(true);
      setErro("");
      setMensagem("");

      const response = await api.post("/user/resgatar-qrcode", {
        codigo: operacaoQRCode.codigo,
        latitude: operacaoQRCode.latitude,
        longitude: operacaoQRCode.longitude,
      });

      setMensagem(
        response.data.message ||
          (operacaoQRCode.tipo_operacao === "debito"
            ? "Compra realizada com sucesso"
            : "QR Code resgatado com sucesso"),
      );

      setOperacaoQRCode(null);

      if (onResgateSucesso) {
        onResgateSucesso();
      }
    } catch (error) {
      const mensagemErro =
        error.response?.data?.message ||
        "Erro ao processar QR Code";

      setErro(mensagemErro);
      setMensagem("");
    } finally {
      setConfirmandoOperacao(false);
    }
  }

  const obterLocalizacaoComFallback = useCallback(async () => {
    if (!navigator.geolocation) {
      return {
        latitude: undefined,
        longitude: undefined,
        aviso:
          "Este navegador não oferece suporte à localização. O resgate pode falhar em QR Codes com restrição de local.",
      };
    }

    try {
      const posicao = await obterPosicao({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 30000,
      });

      return {
        latitude: posicao.coords.latitude,
        longitude: posicao.coords.longitude,
        aviso: "",
      };
    } catch (erroInicial) {
      try {
        const posicao = await obterPosicao({
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        });

        return {
          latitude: posicao.coords.latitude,
          longitude: posicao.coords.longitude,
          aviso: "",
        };
      } catch (erroFinal) {
        return {
          latitude: undefined,
          longitude: undefined,
          aviso: traduzirErroGeolocalizacao(
            erroFinal || erroInicial,
          ),
        };
      }
    }
  }, []);

  useEffect(() => {
    let ativo = true;

    async function iniciarScanner() {
      if (iniciadoRef.current) return;

      iniciadoRef.current = true;

      try {
        setErro("");
        setMensagem("Solicitando acesso à câmera...");

        const html5QrCode = new Html5Qrcode("reader");
        html5QrCodeRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1,
          },
          async (decodedText) => {
            if (!ativo || fechandoRef.current) return;

            try {
              setCarregando(true);
              setErro("");
              setMensagem("QR Code lido. Processando...");

              await pararScanner();

              let codigo = decodedText;

              try {
                const parsed = JSON.parse(decodedText);

                if (parsed.codigo) {
                  codigo = parsed.codigo;
                }
              } catch {
                // usa o texto puro
              }

              setMensagem(
                "QR Code lido. Confirmando localização...",
              );

              const {
                latitude,
                longitude,
                aviso: avisoLocalizacao,
              } = await obterLocalizacaoComFallback();

              const response = await api.post(
                "/user/consultar-qrcode",
                {
                  codigo,
                  latitude,
                  longitude,
                },
              );

              setOperacaoQRCode({
                ...response.data,
                latitude,
                longitude,
              });

              if (avisoLocalizacao) {
                setErro(avisoLocalizacao);
              } else {
                setErro("");
              }

              setMensagem("");
            } catch (error) {
              const mensagemErro =
                error.response?.data?.message ||
                "Erro ao consultar QR Code";

              setErro(mensagemErro);
              setMensagem("");
            } finally {
              setCarregando(false);
            }
          },
          () => {
            // ignora erros contínuos de leitura
          },
        );

        setMensagem(
          "Aponte a câmera para o QR Code",
        );
      } catch (error) {
        console.error(
          "Erro ao iniciar câmera:",
          error,
        );

        setErro(
          "Não foi possível acessar a câmera. No iPhone, verifique a permissão da câmera e da localização no Safari.",
        );

        setMensagem("");
        iniciadoRef.current = false;
      }
    }

    iniciarScanner();

    return () => {
      ativo = false;
      pararScanner();
    };
  }, [
    onResgateSucesso,
    obterLocalizacaoComFallback,
  ]);

  return (
    <div className="scanner-overlay">
      <div className="scanner-modal">
        <h3>Ler QR Code</h3>

        {!operacaoQRCode && (
          <div id="reader" />
        )}

        {mensagem && (
          <p
            style={{
              color: "#00e676",
              marginTop: 12,
            }}
          >
            {mensagem}
          </p>
        )}

        {erro && (
          <p
            style={{
              color: "#ff6b6b",
              marginTop: 12,
            }}
          >
            {erro}
          </p>
        )}

        {operacaoQRCode && (
          <div
            style={{
              marginTop: 16,
              padding: 18,
              borderRadius: 12,
              background:
                "rgba(255,255,255,0.06)",
              border:
                "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
            }}
          >
            <h4
              style={{
                marginTop: 0,
                marginBottom: 14,
              }}
            >
              {operacaoQRCode.tipo_operacao ===
              "debito"
                ? "Confirmar compra"
                : "Confirmar crédito"}
            </h4>

            <p
              style={{
                margin: "8px 0",
              }}
            >
              <strong>
                {operacaoQRCode.nome}
              </strong>
            </p>

            <p
              style={{
                margin: "8px 0",
              }}
            >
              Valor:{" "}
              <strong>
                {Number(
                  operacaoQRCode.valor || 0,
                ).toLocaleString("pt-BR")}{" "}
                Coins
              </strong>
            </p>

            <p
              style={{
                margin: "8px 0",
              }}
            >
              Saldo atual:{" "}
              <strong>
                {Number(
                  operacaoQRCode.saldo_atual || 0,
                ).toLocaleString("pt-BR")}{" "}
                Coins
              </strong>
            </p>

            <p
              style={{
                margin: "8px 0 16px",
              }}
            >
              Saldo após a operação:{" "}
              <strong>
                {Number(
                  operacaoQRCode.saldo_apos_operacao ||
                    0,
                ).toLocaleString("pt-BR")}{" "}
                Coins
              </strong>
            </p>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="action-btn"
                onClick={
                  confirmarOperacaoQRCode
                }
                disabled={
                  confirmandoOperacao
                }
              >
                {confirmandoOperacao
                  ? "Processando..."
                  : operacaoQRCode.tipo_operacao ===
                      "debito"
                    ? "Confirmar compra"
                    : "Confirmar crédito"}
              </button>

              <button
                className="action-btn secondary"
                onClick={handleFechar}
                disabled={
                  confirmandoOperacao
                }
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {!operacaoQRCode && (
          <button
            className="action-btn secondary"
            onClick={handleFechar}
            disabled={carregando}
          >
            Fechar
          </button>
        )}
      </div>
    </div>
  );
}

export default QRCodeScanner;
